import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import KgVis from "../../components/KgVisV2";
import AssociationTable from "../../components/AssociationTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import PathDisplay from "../../components/PathDisplay";
import EntitySearchInput from "../../components/EntitySearchInput";
import { beApiUrl } from "../../api/be_config";
import './Test.css';

export default function Test() {
  const [entityOneId, setEntityOneId] = useState("");
  const [entityOneName, setEntityOneName] = useState([]);
  const [entityTwoId, setEntityTwoId] = useState("");
  const [entityTwoName, setEntityTwoName] = useState([]);
  const [minHop, setMinHop] = useState(3);
  const [maxHop, setMaxHop] = useState(5);
  const [sizeLimit, setSizeLimit] = useState(20);
  
  // 约束相关状态
  const [showConstraints, setShowConstraints] = useState(false);
  const [relationConstraints, setRelationConstraints] = useState({});
  const [nodeTypeConstraints, setNodeTypeConstraints] = useState({});
  const [nodeNameConstraints, setNodeNameConstraints] = useState({});
  const [nodeOntologyConstraints, setNodeOntologyConstraints] = useState({});
  
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [triplets, setTriplets] = useState([]);
  const [paths, setPaths] = useState([]);
  const [pathCount, setPathCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEntityOneId, setIsLoadingEntityOneId] = useState(false);
  const [isLoadingEntityTwoId, setIsLoadingEntityTwoId] = useState(false);
  const [error, setError] = useState(null);
  const [rdyRender, setRdyRender] = useState(false);

  // 可选的关系类型和节点类型
  const relationTypes = [
    'treatment_for', 'hyponym_of', 'characteristic_of', 
    'associated_with', 'risk_factor_of', 'help_diagnose'
  ];
  
  const nodeTypes = [
    'drug', 'disease', 'method', 'signs', 'symptom', 
    'gene', 'physiology', 'region', 'Health_factors'
  ];

  // 约束管理函数
  const addRelationConstraint = (hop, relations) => {
    setRelationConstraints(prev => ({
      ...prev,
      [hop]: relations
    }));
  };

  const removeRelationConstraint = (hop) => {
    setRelationConstraints(prev => {
      const newConstraints = { ...prev };
      delete newConstraints[hop];
      return newConstraints;
    });
  };

  const addNodeTypeConstraint = (hop, nodeTypes) => {
    setNodeTypeConstraints(prev => ({
      ...prev,
      [hop]: nodeTypes
    }));
  };

  const removeNodeTypeConstraint = (hop) => {
    setNodeTypeConstraints(prev => {
      const newConstraints = { ...prev };
      delete newConstraints[hop];
      return newConstraints;
    });
  };

  const addNodeNameConstraint = (hop, nodeNames) => {
    setNodeNameConstraints(prev => ({
      ...prev,
      [hop]: nodeNames
    }));
  };

  const removeNodeNameConstraint = (hop) => {
    setNodeNameConstraints(prev => {
      const newConstraints = { ...prev };
      delete newConstraints[hop];
      return newConstraints;
    });
  };

  const addNodeOntologyConstraint = (hop, ontologies) => {
    setNodeOntologyConstraints(prev => ({
      ...prev,
      [hop]: ontologies
    }));
  };

  const removeNodeOntologyConstraint = (hop) => {
    setNodeOntologyConstraints(prev => {
      const newConstraints = { ...prev };
      delete newConstraints[hop];
      return newConstraints;
    });
  };

  // 生成跳数选项（1到maxHop-1，因为最后一跳到目标节点）
  const getHopOptions = () => {
    const options = [];
    for (let i = 1; i < maxHop; i++) {
      options.push(i);
    }
    return options;
  };

  // 根据entity name获取entity ID的函数
  const getEntityIdByName = async (entityName) => {
    try {
      const response = await fetch(beApiUrl + "kgpage/entity_id_by_name/v1/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entity_name: entityName }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to get entity ID by name.");
      }
      
      const data = await response.json();
      return data.entity_id;
    } catch (error) {
      console.error("Error getting entity ID by name:", error);
      return null;
    }
  };

  // 处理实体1名称变化时自动更新ID
  const handleEntityOneNameChange = async (selectedList) => {
    setEntityOneName(selectedList);
    if (selectedList.length > 0) {
      setIsLoadingEntityOneId(true);
      const entityId = await getEntityIdByName(selectedList[0].name);
      if (entityId) {
        setEntityOneId(entityId);
      }
      setIsLoadingEntityOneId(false);
    } else {
      // 如果清空了名称，也清空ID（可选）
      // setEntityOneId("");
    }
  };

  // 处理实体2名称变化时自动更新ID
  const handleEntityTwoNameChange = async (selectedList) => {
    setEntityTwoName(selectedList);
    if (selectedList.length > 0) {
      setIsLoadingEntityTwoId(true);
      const entityId = await getEntityIdByName(selectedList[0].name);
      if (entityId) {
        setEntityTwoId(entityId);
      }
      setIsLoadingEntityTwoId(false);
    } else {
      // 如果清空了名称，也清空ID（可选）
      // setEntityTwoId("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 验证至少有一个实体的ID或Name（每个实体只需要有ID或Name其中一个即可）
    const hasEntityOne = entityOneId.trim() || (entityOneName.length > 0);
    const hasEntityTwo = entityTwoId.trim() || (entityTwoName.length > 0);
    
    if (!hasEntityOne || !hasEntityTwo) {
      setError("Please provide ID or Name for both entities");
      return;
    }

    setIsLoading(true);
    setError(null);
    setRdyRender(false);
    setNodes([]);
    setEdges([]);
    setTriplets([]);
    setPaths([]);
    setPathCount(0);

    try {
      // 获取实体1的ID - 优先使用ID，没有ID时使用Name
      let finalEntityOneId = entityOneId.trim();
      if (!finalEntityOneId && entityOneName.length > 0) {
        finalEntityOneId = await getEntityIdByName(entityOneName[0].name);
        if (!finalEntityOneId) {
          setError("Could not find ID for source entity name");
          setIsLoading(false);
          return;
        }
      }

      // 获取实体2的ID - 优先使用ID，没有ID时使用Name  
      let finalEntityTwoId = entityTwoId.trim();
      if (!finalEntityTwoId && entityTwoName.length > 0) {
        finalEntityTwoId = await getEntityIdByName(entityTwoName[0].name);
        if (!finalEntityTwoId) {
          setError("Could not find ID for target entity name");
          setIsLoading(false);
          return;
        }
      }

    // 构建约束对象
    const pathConstraints = {};
    if (Object.keys(relationConstraints).length > 0) {
      pathConstraints.relations = relationConstraints;
    }
    if (Object.keys(nodeTypeConstraints).length > 0) {
      pathConstraints.node_types = nodeTypeConstraints;
    }
    if (Object.keys(nodeNameConstraints).length > 0) {
      pathConstraints.node_names = nodeNameConstraints;
    }
    if (Object.keys(nodeOntologyConstraints).length > 0) {
      pathConstraints.node_ontologies = nodeOntologyConstraints;
    }

      const queryData = {
        entity_one_id: finalEntityOneId,
        entity_two_id: finalEntityTwoId,
        min_hop: minHop,
        max_hop: maxHop,
        size_limit: sizeLimit,
        per_level_cap: 200
      };

      // 如果有约束条件，添加到请求中
      if (Object.keys(pathConstraints).length > 0) {
        queryData.path_constraints = pathConstraints;
      }

      const response = await fetch(beApiUrl + "kgpage/n_hop_paths/v1/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(queryData),
      });

      if (!response.ok) {
        throw new Error("Failed to get n-hop paths.");
      }

      const data = await response.json();
      
      if (data && data.nodes && data.edges) {
        console.log(data.triplets);
        console.log(data.nodes);
        console.log(data.paths);
        
        setNodes(data.nodes);
        setEdges(data.edges);
        setTriplets(data.triplets || []);
        setPaths(data.paths || []);
        setPathCount(data.path_count || 0);
        setRdyRender(true);
        
        console.log(data.paths);
      } else {
        setError("No paths found between the specified entities");
      }
    } catch (error) {
      console.error("Error fetching n-hop paths:", error);
      setError("Failed to fetch paths. Please check the entity information and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="test-container">
      {/* Query Form */}
      <Row className="query-section">
        <Col>
          <Form onSubmit={handleSubmit} className="query-form">
            {/* First Row: Entity Information */}
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Entity ID {isLoadingEntityOneId && <small className="text-muted">(Loading...)</small>}</Form.Label>
                  <Form.Control
                    type="text"
                    value={entityOneId}
                    onChange={(e) => setEntityOneId(e.target.value)}
                    placeholder="Enter source entity ID"
                    disabled={isLoading || isLoadingEntityOneId}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <EntitySearchInput
                  value={entityOneName}
                  onChange={handleEntityOneNameChange}
                  placeholder="Search source entity name..."
                  label="Source Entity Name"
                  disabled={isLoading}
                />
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Entity ID {isLoadingEntityTwoId && <small className="text-muted">(Loading...)</small>}</Form.Label>
                  <Form.Control
                    type="text"
                    value={entityTwoId}
                    onChange={(e) => setEntityTwoId(e.target.value)}
                    placeholder="Enter target entity ID"
                    disabled={isLoading || isLoadingEntityTwoId}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <EntitySearchInput
                  value={entityTwoName}
                  onChange={handleEntityTwoNameChange}
                  placeholder="Search target entity name..."
                  label="Target Entity Name"
                  disabled={isLoading}
                />
              </Col>
            </Row>
            
            {/* Second Row: Search Parameters */}
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Min Hops: {minHop}</Form.Label>
                  <Form.Range
                    min={1}
                    max={9}
                    value={minHop}
                    onChange={(e) => {
                      const newMinHop = parseInt(e.target.value);
                      setMinHop(newMinHop);
                      if (newMinHop >= maxHop) {
                        setMaxHop(newMinHop + 1);
                      }
                    }}
                    disabled={isLoading}
                    className="hop-slider"
                  />
                  <div className="slider-labels">
                    <span>1</span>
                    <span>9</span>
                  </div>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Hops: {maxHop}</Form.Label>
                  <Form.Range
                    min={minHop + 1}
                    max={10}
                    value={maxHop}
                    onChange={(e) => setMaxHop(parseInt(e.target.value))}
                    disabled={isLoading}
                    className="hop-slider"
                  />
                  <div className="slider-labels">
                    <span>{minHop + 1}</span>
                    <span>10</span>
                  </div>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Path Limit</Form.Label>
                  <Form.Control
                    type="number"
                    value={sizeLimit}
                    onChange={(e) => setSizeLimit(parseInt(e.target.value))}
                    min={10}
                    max={1000}
                    disabled={isLoading}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={isLoading}
                  className="query-btn w-100"
                >
                  {isLoading ? "Searching..." : "Search"}
                </Button>
              </Col>
              <Col md={4}></Col>
            </Row>

            {error && (
              <Alert variant="danger" className="mt-3 mb-0">
                {error}
              </Alert>
            )}

            {/* 约束设置按钮 */}
            <div className="mt-3 mb-0">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setShowConstraints(!showConstraints)}
                disabled={isLoading}
              >
                {showConstraints ? 'Hide Constraints' : 'Add Path Constraints'}
              </Button>
            </div>

            {/* {pathCount > 0 && (
              <Alert variant="success" className="mt-3 mb-0">
                Found {pathCount} paths (up to {maxHop} hops)
              </Alert>
            )} */}
          </Form>
        </Col>
      </Row>

      {/* 约束设置面板 */}
      {showConstraints && (
        <Row className="constraints-section">
          <Col>
            <div className="constraints-panel p-3 border rounded bg-light">
              <h6>Path Constraints</h6>
              
              {/* 关系约束 */}
              <div className="constraint-group mb-3">
                <h6 className="text-muted">Relation Constraints</h6>
                <small className="text-muted">Specify required relations for specific hops</small>
                
                {Object.entries(relationConstraints).map(([hop, relations]) => (
                  <div key={hop} className="constraint-item mb-2 p-2 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span><strong>Hop {hop}:</strong> {relations.join(', ')}</span>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeRelationConstraint(hop)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                
                <div className="add-constraint-form">
                  <Row>
                    <Col md={3}>
                      <Form.Select 
                        id="relation-hop-select"
                        size="sm"
                      >
                        <option value="">Select Hop</option>
                        {getHopOptions().map(hop => (
                          <option key={hop} value={hop}>Hop {hop}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Select 
                        id="relation-type-select"
                        size="sm" 
                        multiple
                      >
                        {relationTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => {
                          const hopSelect = document.getElementById('relation-hop-select');
                          const typeSelect = document.getElementById('relation-type-select');
                          const selectedHop = hopSelect.value;
                          const selectedTypes = Array.from(typeSelect.selectedOptions).map(option => option.value);
                          
                          if (selectedHop && selectedTypes.length > 0) {
                            addRelationConstraint(selectedHop, selectedTypes);
                            hopSelect.value = '';
                            typeSelect.selectedIndex = -1;
                          }
                        }}
                      >
                        Add Relation
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>

              {/* 节点类型约束 */}
              <div className="constraint-group mb-3">
                <h6 className="text-muted">Node Type Constraints</h6>
                <small className="text-muted">Specify required node types for intermediate positions</small>
                
                {Object.entries(nodeTypeConstraints).map(([hop, types]) => (
                  <div key={hop} className="constraint-item mb-2 p-2 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span><strong>Position {hop}:</strong> {types.join(', ')}</span>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeNodeTypeConstraint(hop)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                
                <div className="add-constraint-form">
                  <Row>
                    <Col md={3}>
                      <Form.Select 
                        id="node-hop-select"
                        size="sm"
                      >
                        <option value="">Select Position</option>
                        {getHopOptions().map(hop => (
                          <option key={hop} value={hop}>Position {hop}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Select 
                        id="node-type-select"
                        size="sm" 
                        multiple
                      >
                        {nodeTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => {
                          const hopSelect = document.getElementById('node-hop-select');
                          const typeSelect = document.getElementById('node-type-select');
                          const selectedHop = hopSelect.value;
                          const selectedTypes = Array.from(typeSelect.selectedOptions).map(option => option.value);
                          
                          if (selectedHop && selectedTypes.length > 0) {
                            addNodeTypeConstraint(selectedHop, selectedTypes);
                            hopSelect.value = '';
                            typeSelect.selectedIndex = -1;
                          }
                        }}
                      >
                        Add Node Type
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>

              {/* 节点名称约束 */}
              <div className="constraint-group mb-3">
                <h6 className="text-muted">Node Name Constraints</h6>
                <small className="text-muted">Specify required node names for intermediate positions (comma-separated)</small>
                
                {Object.entries(nodeNameConstraints).map(([hop, names]) => (
                  <div key={hop} className="constraint-item mb-2 p-2 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span><strong>Position {hop}:</strong> {names.join(', ')}</span>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeNodeNameConstraint(hop)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                
                <div className="add-constraint-form">
                  <Row>
                    <Col md={3}>
                      <Form.Select 
                        id="node-name-hop-select"
                        size="sm"
                      >
                        <option value="">Select Position</option>
                        {getHopOptions().map(hop => (
                          <option key={hop} value={hop}>Position {hop}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        id="node-name-input"
                        type="text"
                        size="sm"
                        placeholder="Enter node names (comma-separated)"
                      />
                    </Col>
                    <Col md={3}>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => {
                          const hopSelect = document.getElementById('node-name-hop-select');
                          const nameInput = document.getElementById('node-name-input');
                          const selectedHop = hopSelect.value;
                          const inputNames = nameInput.value.trim();
                          
                          if (selectedHop && inputNames) {
                            const names = inputNames.split(',').map(name => name.trim()).filter(name => name);
                            if (names.length > 0) {
                              addNodeNameConstraint(selectedHop, names);
                              hopSelect.value = '';
                              nameInput.value = '';
                            }
                          }
                        }}
                      >
                        Add Names
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>

              {/* 节点本体约束 */}
              <div className="constraint-group">
                <h6 className="text-muted">Node Ontology Constraints</h6>
                <small className="text-muted">Specify required ontologies for intermediate positions (comma-separated)</small>
                
                {Object.entries(nodeOntologyConstraints).map(([hop, ontologies]) => (
                  <div key={hop} className="constraint-item mb-2 p-2 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span><strong>Position {hop}:</strong> {ontologies.join(', ')}</span>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeNodeOntologyConstraint(hop)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                
                <div className="add-constraint-form">
                  <Row>
                    <Col md={3}>
                      <Form.Select 
                        id="node-ontology-hop-select"
                        size="sm"
                      >
                        <option value="">Select Position</option>
                        {getHopOptions().map(hop => (
                          <option key={hop} value={hop}>Position {hop}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        id="node-ontology-input"
                        type="text"
                        size="sm"
                        placeholder="Enter ontologies (comma-separated, e.g., UMLS, MONDO)"
                      />
                    </Col>
                    <Col md={3}>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => {
                          const hopSelect = document.getElementById('node-ontology-hop-select');
                          const ontologyInput = document.getElementById('node-ontology-input');
                          const selectedHop = hopSelect.value;
                          const inputOntologies = ontologyInput.value.trim();
                          
                          if (selectedHop && inputOntologies) {
                            const ontologies = inputOntologies.split(',').map(ont => ont.trim()).filter(ont => ont);
                            if (ontologies.length > 0) {
                              addNodeOntologyConstraint(selectedHop, ontologies);
                              hopSelect.value = '';
                              ontologyInput.value = '';
                            }
                          }
                        }}
                      >
                        Add Ontologies
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Graph Visualization - Always show with reserved space */}
      <Row className="graph-section">
        <Col>
          <div className="graph-container">
            {rdyRender ? (
              <KgVis nodes={nodes} edges={edges} />
            ) : (
              <div className="graph-placeholder">
                {isLoading ? (
                  <LoadingSpinner 
                    size="xl"
                    variant="primary"
                    title="Loading N-hop Paths"
                    subtitle="Searching for connections between entities"
                    minHeight="400px"
                  />
                ) : (
                  <div className="empty-message">
                    <p>Enter entity IDs and click Search to visualize paths</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Path Display Section */}
      {rdyRender && (
        <Row className="path-section">
          <Col>
            <PathDisplay 
              paths={paths} 
              entityOneId={entityOneId || (entityOneName.length > 0 ? entityOneName[0].name : '')} 
              entityTwoId={entityTwoId || (entityTwoName.length > 0 ? entityTwoName[0].name : '')} 
              pathCount={pathCount} 
            />
          </Col>
        </Row>
      )}

      {/* Table Section */}
      {rdyRender && (
        <Row className="table-section">
          <Col>
            <AssociationTable triplets={triplets} />
          </Col>
        </Row>
      )}
    </Container>
  );
}
